import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import Auth from '../../api/auth';
import GitlabWikiHelpButton from '../../components/GitlabWikiHelpButton';
import Table from '../../components/Table';
import useApiQuery from '../../hooks/useApiQuery';
import useAppContext from '../../hooks/useAppContext';

import type { APIHookArg, ErrorType } from '@/types';
import type { Organization } from '@/types/organization';
import type { TableRowData } from '@/types/tableTypes';

const organizationsGuideSlug = 'user-guide/organizations';

const Organizations: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const contextState = useAppContext();

  const [organizations, setOrganizations] = useState<Organization[]>([]);

  const { error: fetchError, makeApiCall: getOrganizationsCall } = useApiQuery(
    Auth.fetchV1Organizations as unknown as APIHookArg
  );

  useEffect(() => {
    const getOrganizations = async (): Promise<void> => {
      try {
        const result = (await getOrganizationsCall()) as Organization[];
        setOrganizations(result);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };
    getOrganizations();
    contextState.setSelectedOrganization?.(null);
  }, [contextState, getOrganizationsCall]);

  const fields = [
    {
      title: { id: 'name' },
      id: 'enterpriseName',
    },
    {
      title: { id: 'crmId' },
      id: 'crmid',
    },
    {
      title: { id: 'accountOwner' },
      id: 'accountowner',
    },
  ];

  const rows = organizations.map(org => ({
    key: org._id ?? '',
    onRowClick: () => {
      contextState.setSelectedOrganization?.(org);
      navigate(`/governance/organizations/${org._id ?? ''}`);
    },
    data: [
      {
        field: 'enterpriseName',
        value: org.enterpriseName,
      },
      {
        field: 'crmid',
        value: org.CRMId,
      },
      {
        field: 'accountowner',
        value: org.accountOwner?.email,
      },
    ],
  })) as TableRowData[];

  return (
    <Table
      header={intl.formatMessage({ id: 'organizations' })}
      fields={fields}
      rows={rows}
      error={fetchError as ErrorType}
      headerActionSlot={{
        content: (
          <GitlabWikiHelpButton
            slug={organizationsGuideSlug}
            tooltipMessageId='organizationsGuide'
            tooltipDefaultMessage='Organizations guide'
            modalTitleMessageId='organizationsGuide'
            modalTitleDefaultMessage='Organizations guide'
            buttonAriaLabel={intl.formatMessage({ id: 'organizationsGuideAriaLabel' })}
            buttonSx={{ mt: 0 }}
          />
        ),
      }}
      // Distinct key: GovernanceTabs mounts this and the Users table on the
      // same route, so they'd otherwise share (and bleed into) the 'search' slot.
      searchParamKey='governanceOrganizationsSearch'
    />
  );
};

export default Organizations;
